import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { UserAuth } from "./AuthContext";
import { getUserProfile } from "../services/profileApi";
import { supabase } from "../lib/supabase";

interface ProfilePhoto {
  avatarUrl: string | null;
  initials: string;
}

const ProfileContext = createContext<any>(null);

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
  const { session } = UserAuth();

  const [profile, setProfile] = useState<any>(null);
  const [photo, setPhoto] = useState<ProfilePhoto>({
    avatarUrl: null,
    initials: "LP",
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef<string | null>(null);

  const loadProfile = useCallback(async () => {
    if (!session) {
      console.log("[ProfileContext] No session, clearing profile");
      setProfile(null);
      setPhoto({ avatarUrl: null, initials: "LP" });
      return;
    }

    const uid = session?.user?.id ?? null;
    const role = session?.user?.user_metadata?.role ?? null;
    const email = session?.user?.email;

    console.log(
      `[ProfileContext] Loading profile for ${email} (${uid}) | Role: ${role}`,
    );

    if (!role) {
      console.warn("[ProfileContext] No role found in user_metadata");
      setProfile(null);
      return;
    }

    try {
      const data = await getUserProfile(uid, role);

      setProfile(data);

      // Update photo initials when profile loads
      if (data) {
        const firstInitial = data.first_name?.[0] || "";
        const lastInitial = data.last_name?.[0] || "";
        const initials = `${firstInitial}${lastInitial}`.toUpperCase() || "LP";

        setPhoto({
          avatarUrl: data.avatar_url || null,
          initials,
        });
      }
    } catch {
      // silently fail — don't rethrow, don't trigger re-render
      setProfile(null);
    }
  }, [session]);

  // Upload avatar
  const uploadAvatar = async (file: File) => {
    setUploading(true);
    setError(null);

    try {
      const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!validTypes.includes(file.type)) {
        throw new Error("Please upload a JPG, PNG, GIF, or WebP image");
      }

      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error("Image must be less than 2MB");
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      console.log("[Upload] Starting upload for user:", user.id);

      // IMPORTANT: Delete all existing files in user's folder first
      // This ensures we replace the old photo regardless of file extension
      const { data: existingFiles, error: listError } = await supabase.storage
        .from("profile-photos")
        .list(user.id);

      if (listError) {
        console.error("[Upload] Error listing files:", listError);
        throw listError;
      }

      if (existingFiles && existingFiles.length > 0) {
        console.log("[Upload] Found existing files:", existingFiles.length);
        const filesToDelete = existingFiles.map((f) => `${user.id}/${f.name}`);
        const { error: deleteError } = await supabase.storage
          .from("profile-photos")
          .remove(filesToDelete);

        if (deleteError) {
          console.error("[Upload] Error deleting files:", deleteError);
          throw deleteError;
        }

        console.log(
          "[Upload] Deleted old files, waiting for storage to process...",
        );
        // Wait 500ms to ensure storage has processed the deletion
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      // Upload new file with consistent naming
      const fileExt = file.name.split(".").pop();
      const filePath = `${user.id}/avatar.${fileExt}`;

      console.log("[Upload] Uploading to path:", filePath);

      const { error: uploadError, data: uploadData } = await supabase.storage
        .from("profile-photos")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: true, // Use upsert to handle any timing issues
        });

      if (uploadError) {
        console.error("[Upload] Upload error:", uploadError);
        throw uploadError;
      }

      console.log("[Upload] Upload successful:", uploadData);

      const {
        data: { publicUrl },
      } = supabase.storage.from("profile-photos").getPublicUrl(filePath);

      // Create cache-busted URL to force browser refresh
      const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;

      console.log("[Upload] Updating database with URL:", cacheBustedUrl);

      // Determine table based on role
      const role = user.user_metadata?.role;
      if (!role) {
        throw new Error("User role not found");
      }
      const table = `${role}s`; // "students" or "counselors"

      console.log(`[Upload] Updating table: ${table}`);

      // Save the cache-busted URL to database for consistency
      const { error: dbError } = await supabase
        .from(table)
        .update({ avatar_url: cacheBustedUrl })
        .eq("auth_user_id", user.id);

      if (dbError) {
        console.error("[Upload] Database update error:", dbError);
        throw dbError;
      }

      console.log("[Upload] Database updated successfully");

      setPhoto((prev) => ({ ...prev, avatarUrl: cacheBustedUrl }));

      // Reload profile to sync avatar_url
      await loadProfile();

      console.log("[Upload] Upload complete!");
      return cacheBustedUrl;
    } catch (err: any) {
      console.error("[Upload] Failed:", err);
      setError(err.message || "Failed to upload photo");
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // Remove avatar
  const removeAvatar = async () => {
    setUploading(true);
    setError(null);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: files } = await supabase.storage
        .from("profile-photos")
        .list(user.id);

      if (files && files.length > 0) {
        const filesToDelete = files.map((file) => `${user.id}/${file.name}`);
        await supabase.storage.from("profile-photos").remove(filesToDelete);
      }

      // Determine table based on role
      const role = user.user_metadata?.role;
      if (!role) {
        throw new Error("User role not found");
      }
      const table = `${role}s`;

      const { error: dbError } = await supabase
        .from(table)
        .update({ avatar_url: null })
        .eq("auth_user_id", user.id);

      if (dbError) throw dbError;

      setPhoto((prev) => ({ ...prev, avatarUrl: null }));

      // Reload profile to sync avatar_url
      await loadProfile();
    } catch (err: any) {
      setError(err.message || "Failed to remove photo");
      throw err;
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!session) {
      setProfile(null);
      hasFetchedRef.current = null;
      return;
    }

    // Only fetch if the session user ID actually changed
    const uid = session?.user?.id;
    if (hasFetchedRef.current === uid) return;
    hasFetchedRef.current = uid;
    loadProfile();
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateProfileTheme = (theme: "light" | "dark" | "system") => {
    if (profile) {
      setProfile((prev: any) => ({ ...prev, preferred_theme: theme }));
    }
  };

  // Apply theme globally based on profile.preferred_theme
  useEffect(() => {
    // Determine target theme (default to "dark" to preserve the app's dark theme)
    const currentTheme = profile?.preferred_theme || "dark";

    const apply = (theme: "light" | "dark" | "system") => {
      const el = document.documentElement;
      if (theme === "dark") {
        el.classList.add("dark");
      } else if (theme === "light") {
        el.classList.remove("dark");
      } else {
        const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
        if (prefersDark.matches) el.classList.add("dark");
        else el.classList.remove("dark");
      }
    };

    apply(currentTheme);

    let mql: MediaQueryList | null = null;
    const onChange = (e: MediaQueryListEvent) => {
      if (currentTheme === "system") {
        const el = document.documentElement;
        if (e.matches) el.classList.add("dark");
        else el.classList.remove("dark");
      }
    };

    if (currentTheme === "system") {
      mql = window.matchMedia("(prefers-color-scheme: dark)");
      mql.addEventListener?.("change", onChange);
    }

    return () => {
      if (mql) mql.removeEventListener?.("change", onChange);
    };
  }, [profile?.preferred_theme]);

  // Sync theme changes initiated from Preferences panels immediately
  // This helps when hooks trigger a change before DB updates the context
  useEffect(() => {
    // Optional: add a global event listener so we can trigger a profile reload
    // when setting changes.
  }, []);

  return (
    <ProfileContext.Provider
      value={{
        profile,
        loadProfile,
        photo,
        uploading,
        error,
        uploadAvatar,
        removeAvatar,
        updateProfileTheme,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const UserProfile = () => useContext(ProfileContext);
